import type {
  ParsedIndicatorMeta,
  ParsedIndicatorInput,
  ValidationResult,
} from "./types";
import type { CatalogParameter } from "@/lib/chart-indicators/catalog";
import type { CompileInputMeta } from "./runtime-types";

export { DEFAULT_INDICATOR_CODE } from "./default-indicator-code";

const APPLIED_PRICE_OPTIONS = [
  "Close",
  "Open",
  "High",
  "Low",
  "Median",
  "Typical",
];

const TIMEFRAME_OPTIONS = [
  "M1",
  "M5",
  "M15",
  "M30",
  "H1",
  "H4",
  "D1",
  "W1",
  "MN",
];

const LINE_STYLE_OPTIONS = [
  "Line",
  "Histogram",
  "Dashed",
  "Dotted",
  "StepLine",
  "Arrow",
  "ColorLine",
  "ColorHistogram",
  "ZigZag",
];

function parseAttributeValue(attrBody: string, key: string): string | undefined {
  const re = new RegExp(`${key}\\s*=\\s*("([^"]*)"|([^,\\)\\s]+))`, "i");
  const m = attrBody.match(re);
  if (!m) return undefined;
  if (m[2] != null) return m[2];
  return m[3]?.trim();
}

function parseBoolAttr(value: string | undefined): boolean | undefined {
  if (value == null) return undefined;
  const v = value.toLowerCase();
  if (v === "true") return true;
  if (v === "false") return false;
  return undefined;
}

function parseNumericAttr(value: string | undefined): number | undefined {
  if (value == null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export function parseIndicatorMeta(code: string): ParsedIndicatorMeta {
  const meta: ParsedIndicatorMeta = {};

  const indicatorAttr = code.match(/\[Indicator\s*\(([\s\S]*?)\)\]/i);
  if (indicatorAttr) {
    const body = indicatorAttr[1];
    meta.name = parseAttributeValue(body, "Name");
    meta.version = parseAttributeValue(body, "Version");
    const overlay = parseBoolAttr(parseAttributeValue(body, "Overlay"));
    if (overlay != null) meta.overlay = overlay;
  }

  const classMatch = code.match(
    /public\s+class\s+(\w+)\s*:\s*Indicator\b/i
  );
  if (classMatch) {
    meta.className = classMatch[1];
  }

  return meta;
}

function mapIndicatorTypeToField(
  sourceType: string,
  inputBody: string
): Pick<ParsedIndicatorInput, "type" | "options" | "min" | "max" | "step"> {
  const t = sourceType.trim();
  if (t === "bool") {
    return { type: "bool" };
  }
  if (t === "string") {
    return { type: "string" };
  }
  if (t === "Color") {
    return { type: "color" };
  }
  if (t === "AppliedPrice") {
    return { type: "select", options: [...APPLIED_PRICE_OPTIONS] };
  }
  if (t === "TimeFrame") {
    return { type: "select", options: [...TIMEFRAME_OPTIONS] };
  }
  if (t === "LineStyle") {
    return { type: "select", options: [...LINE_STYLE_OPTIONS] };
  }
  if (t === "int" || t === "long" || t === "double" || t === "float") {
    return {
      type: "number",
      min: parseNumericAttr(parseAttributeValue(inputBody, "Min")),
      max: parseNumericAttr(parseAttributeValue(inputBody, "Max")),
      step: parseNumericAttr(parseAttributeValue(inputBody, "Step")),
    };
  }
  return { type: "string" };
}

function parseDefaultLiteral(
  raw: string,
  fieldType: ParsedIndicatorInput["type"]
): number | string | boolean {
  const trimmed = raw.trim();
  if (fieldType === "bool") {
    return trimmed.toLowerCase() === "true";
  }
  if (fieldType === "number") {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (fieldType === "color") {
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      const inner = trimmed.slice(1, -1);
      if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(inner)) return inner;
    }
    if (trimmed.startsWith("Color.")) {
      return trimmed.replace("Color.", "#2962FF");
    }
    if (trimmed.includes("FromHex")) {
      const hex = trimmed.match(/FromHex\("([^"]+)"\)/i);
      if (hex) return hex[1];
    }
    return "#2962FF";
  }
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1);
  }
  return trimmed.replace(/;$/, "");
}

const INPUT_REGEX =
  /\[Input(?:\s*\(([\s\S]*?)\))?\]\s*public\s+(\w+)\s+(\w+)\s*\{\s*get;\s*set;\s*\}\s*=\s*([^;]+);/gi;

export function parseInputs(code: string): ParsedIndicatorInput[] {
  const inputs: ParsedIndicatorInput[] = [];
  let match: RegExpExecArray | null;

  while ((match = INPUT_REGEX.exec(code)) !== null) {
    const inputBody = match[1] ?? "";
    const sourceType = match[2];
    const propName = match[3];
    const defaultRaw = match[4];

    const fieldMeta = mapIndicatorTypeToField(sourceType, inputBody);
    const label =
      parseAttributeValue(inputBody, "Label") ?? propName;
    const group = parseAttributeValue(inputBody, "Group");

    inputs.push({
      name: propName,
      label,
      group,
      defaultValue: parseDefaultLiteral(defaultRaw, fieldMeta.type),
      ...fieldMeta,
    });
  }

  return inputs;
}

export function validateIndicator(code: string): ValidationResult {
  const errors: string[] = [];

  if (
    !/using\s+Vybe\.Indicators\s*;/i.test(code) &&
    !/using\s+ViiScript\.Indicators\s*;/i.test(code)
  ) {
    errors.push('Missing required import: using Vybe.Indicators;');
  }

  const indicatorAttr = code.match(/\[Indicator\s*\([\s\S]*?\)\]/i);
  if (!indicatorAttr) {
    errors.push("Missing [Indicator(...)] attribute on the class");
  } else {
    const body = indicatorAttr[0];
    if (!/Name\s*=/i.test(body)) {
      errors.push("[Indicator] must include a Name property");
    }
  }

  const classMatch = code.match(
    /public\s+class\s+(\w+)\s*:\s*Indicator\b/i
  );
  if (!classMatch) {
    errors.push("Missing public class that inherits from Indicator");
  }

  const hasOnInit = /override\s+void\s+OnInit\s*\(/i.test(code);
  const hasOnCalculate = /override\s+void\s+OnCalculate\s*\(/i.test(code);
  if (!hasOnInit && !hasOnCalculate) {
    errors.push("Indicator must override OnInit() and/or OnCalculate()");
  }

  return { ok: errors.length === 0, errors };
}

/** Map compiler-discovered inputs to ParsedIndicatorInput for the inputs form UI */
export function compileInputsToParsedInputs(
  inputs: CompileInputMeta[]
): ParsedIndicatorInput[] {
  return inputs.map((input) => ({
    name: input.name,
    label: input.label || input.name,
    group: input.group,
    type:
      input.type === "bool"
        ? "bool"
        : input.type === "color"
          ? "color"
          : input.type === "string"
            ? "string"
            : "number",
    defaultValue:
      input.defaultValue ??
      (input.type === "number"
        ? 0
        : input.type === "bool"
          ? false
          : input.type === "color"
            ? "#2962FF"
            : ""),
    min: input.min,
    max: input.max,
    step: input.step,
  }));
}

/** Map compiler-discovered inputs to catalog parameters */
export function compileInputsToCatalogParameters(
  inputs: CompileInputMeta[]
): CatalogParameter[] {
  const parsed: ParsedIndicatorInput[] = inputs.map((input) => ({
    name: input.name,
    label: input.label,
    type:
      input.type === "bool"
        ? "bool"
        : input.type === "color"
          ? "color"
        : input.type === "string"
          ? "string"
          : "number",
    defaultValue:
      input.defaultValue ??
      (input.type === "number"
        ? 0
        : input.type === "bool"
          ? false
          : input.type === "color"
            ? "#2962FF"
            : ""),
    group: input.group,
    min: input.min,
    max: input.max,
    step: input.step,
  }));
  return parsedInputsToCatalogParameters(parsed);
}

/** Map parsed inputs to catalog parameters for IndicatorParameters UI */
export function parsedInputsToCatalogParameters(
  inputs: ParsedIndicatorInput[]
): CatalogParameter[] {
  return inputs.map((input) => {
    if (input.type === "number") {
      return {
        name: input.name,
        label: input.label,
        type: "number",
        defaultValue:
          typeof input.defaultValue === "number"
            ? input.defaultValue
            : Number(input.defaultValue) || 0,
        min: input.min,
        max: input.max,
        step: input.step,
      };
    }
    if (input.type === "select" && input.options?.length) {
      const def =
        typeof input.defaultValue === "string"
          ? input.defaultValue
          : String(input.defaultValue);
      return {
        name: input.name,
        label: input.label,
        type: "select",
        defaultValue: input.options.includes(def) ? def : input.options[0],
        options: input.options,
      };
    }
    if (input.type === "color") {
      const def =
        typeof input.defaultValue === "string"
          ? input.defaultValue
          : "#2962FF";
      return {
        name: input.name,
        label: input.label,
        type: "color",
        defaultValue: def,
      };
    }
    if (input.type === "bool") {
      return {
        name: input.name,
        label: input.label,
        type: "select",
        defaultValue: input.defaultValue === true ? "true" : "false",
        options: ["true", "false"],
      };
    }
    return {
      name: input.name,
      label: input.label,
      type: "select",
      defaultValue:
        typeof input.defaultValue === "string"
          ? input.defaultValue
          : String(input.defaultValue ?? ""),
      options: undefined,
    };
  });
}

export function formatCustomParamSummary(
  values: Record<string, number | string> | undefined
): string {
  if (!values) return "";
  return Object.entries(values)
    .slice(0, 3)
    .map(([k, v]) => `${k} ${v}`)
    .join(" · ");
}

export function getCustomIndicatorIdFromInstanceId(
  indicatorId: string
): string | null {
  if (!indicatorId.startsWith("custom_")) return null;
  return indicatorId.slice("custom_".length);
}
